import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

@observer
class GeofenceStateList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            geofenceData: null,
            loading: true,
            collapsed: true,
            selectedRaceClass: null,
            showRawJson: false,
            copied: false,
        };
        this.reloadTimer = null;
    }

    componentDidMount() {
        this.loadGeofenceStates();
        this.startAutoReload();
    }

    componentDidUpdate(prevProps) {
        const { raceStore } = this.props.stores;
        const { race } = raceStore;
        const prevRaceStore = prevProps.stores.raceStore;
        const prevRace = prevRaceStore.race;
        if (race?.id && race.id !== prevRace?.id) {
            this.loadGeofenceStates();
        }
    }

    componentWillUnmount() {
        this.stopAutoReload();
    }

    startAutoReload = () => {
        this.stopAutoReload();
        // Reload every 30 seconds
        this.reloadTimer = setInterval(() => {
            this.refreshGeofenceStates();
        }, 30 * 1000);
    }

    stopAutoReload = () => {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
    }

    // Silent refresh without loading state (no flickering)
    refreshGeofenceStates = async () => {
        const { raceStore } = this.props.stores;
        const { race } = raceStore;
        if (!race?.id) {
            return;
        }
        const geofenceData = await raceStore.getGeofenceStates(race.id);
        // Keep current selectedRaceClass
        this.setState({ geofenceData });
    }

    loadGeofenceStates = async () => {
        const { raceStore } = this.props.stores;
        const { race } = raceStore;
        if (!race?.id) {
            return;
        }
        this.setState({ loading: true });
        const geofenceData = await raceStore.getGeofenceStates(race.id);
        // Auto-select first race class if available
        let selectedRaceClass = this.state.selectedRaceClass;
        if (!selectedRaceClass && geofenceData?.raceClasses?.length > 0) {
            selectedRaceClass = geofenceData.raceClasses[0].id;
        }
        this.setState({ geofenceData, loading: false, selectedRaceClass });
    }

    toggleCollapsed = () => {
        this.setState({ collapsed: !this.state.collapsed });
    }

    selectRaceClass = (e) => {
        this.setState({ selectedRaceClass: parseInt(e.target.value, 10) });
    }

    toggleRawJson = () => {
        this.setState({ showRawJson: !this.state.showRawJson, copied: false });
    }

    copyToClipboard = () => {
        const { geofenceData } = this.state;
        const { raceStore } = this.props.stores;
        const { race } = raceStore;

        const debugPayload = {
            race: {
                id: race.id,
                title: race.title,
                startDate: race.startDate,
                endDate: race.endDate,
                addAutoResultsHidden: race.addAutoResultsHidden,
                showResults: race.showResults,
                showTracking: race.showTracking,
            },
            geofenceData,
        };

        const prompt = `Here is the geofence debug data for race "${race.title}" (id: ${race.id}). Please analyze the geofence states and help me understand why auto results are not appearing correctly:

\`\`\`json
${JSON.stringify(debugPayload, null, 2)}
\`\`\``;

        navigator.clipboard.writeText(prompt).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        });
    }

    getStateForCheckpoint = (contestant, checkpointMd5) => {
        return contestant.states?.find(s => s.checkpointMd5 === checkpointMd5);
    }

    // Get actual race result for checkpoint (source of truth)
    getActualResult = (contestant, checkpointMd5) => {
        return contestant.actualResults?.[checkpointMd5] || { hasIn: false, hasOut: false };
    }

    // Determine "unlocked" checkpoint - based on ACTUAL RESULTS, not geofence state
    getUnlockedCheckpoint = (contestant, checkpoints) => {
        if (!checkpoints || checkpoints.length === 0) return null;

        for (let i = 0; i < checkpoints.length; i++) {
            const cp = checkpoints[i];
            const result = this.getActualResult(contestant, cp.md5);
            const isStart = i === 0;
            const isFinish = i === checkpoints.length - 1;

            // For START: just need departure (hasOut)
            if (isStart) {
                if (!result.hasOut) {
                    return cp.md5;
                }
                continue;
            }

            // For FINISH: just need arrival (hasIn)
            if (isFinish) {
                if (!result.hasIn) {
                    return cp.md5;
                }
                continue;
            }

            // For intermediate: need departure (hasOut) to be "passed"
            // (if they have hasIn but not hasOut, they're still at the checkpoint)
            if (!result.hasOut) {
                return cp.md5;
            }
        }
        // All checkpoints completed
        return null;
    }

    // Render cell based on ACTUAL RESULTS (source of truth) + geofence state for GPS info
    renderCellStatus = (actualResult, geoState, isUnlocked, isFinish, isStart) => {
        const { hasIn, hasOut, isAutoIn, isAutoOut } = actualResult;
        const gpsInfo = geoState ? `GPS: ${geoState.pointsInsideFence || 0}/${geoState.pointsOutsideFence || 0}` : 'No GPS data';

        // FINISH checkpoint - just needs arrival
        if (isFinish && hasIn) {
            return (
                <span class='badge bg-success' title={`Finished${isAutoIn ? ' (auto)' : ' (manual)'} - ${gpsInfo}`}>
                    <i class='fa-solid fa-flag-checkered' />
                </span>
            );
        }

        // START checkpoint - just needs departure
        if (isStart) {
            if (hasOut) {
                return (
                    <span class='badge bg-success' title={`Started${isAutoOut ? ' (auto)' : ' (manual)'} - ${gpsInfo}`}>
                        <i class='fa-solid fa-check' />
                    </span>
                );
            }
            // At start but not departed
            if (isUnlocked) {
                return (
                    <span class='badge bg-warning text-dark' title={`At start - waiting to depart - ${gpsInfo}`}>
                        <i class='fa-solid fa-hourglass-start' />
                    </span>
                );
            }
        }

        // Intermediate checkpoint - needs both in and out
        if (hasIn && hasOut) {
            return (
                <span class='badge bg-success' title={`Completed${isAutoIn || isAutoOut ? ' (auto)' : ' (manual)'} - ${gpsInfo}`}>
                    <i class='fa-solid fa-check' />
                </span>
            );
        }

        // Has arrival but no departure - currently at checkpoint
        if (hasIn && !hasOut) {
            return (
                <span class='badge bg-info' title={`At checkpoint${isAutoIn ? ' (auto)' : ' (manual)'} - ${gpsInfo}`}>
                    <i class='fa-solid fa-location-dot' />
                </span>
            );
        }

        // Has GPS data but no results yet
        if (geoState && (geoState.pointsInsideFence > 0 || geoState.pointsOutsideFence > 0)) {
            const geoStateValue = geoState.state;
            if (geoStateValue === 'inside' || geoStateValue === 'entering') {
                return (
                    <span class='badge bg-primary' title={`GPS: ${geoStateValue} - ${geoState.pointsInsideFence || 0}/${geoState.pointsOutsideFence || 0} pts, no result yet`}>
                        <i class='fa-solid fa-satellite-dish' />
                    </span>
                );
            }
        }

        // Unlocked - next checkpoint to process
        if (isUnlocked) {
            return (
                <span class='badge bg-warning text-dark' title={`Unlocked - next checkpoint - ${gpsInfo}`}>
                    <i class='fa-solid fa-unlock' />
                </span>
            );
        }

        // Locked - not yet reached
        return <span class='text-muted' title='Not yet reached'>-</span>;
    }

    render() {
        const { geofenceData, loading, collapsed, selectedRaceClass } = this.state;
        const { userStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Only visible to super admin
        if (!isAdmin) {
            return null;
        }

        if (loading) {
            return (
                <div class={`mx-3 mt-2 mb-3 p-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-light'}`}>
                    <div class='d-flex align-items-center'>
                        <div class='spinner-border spinner-border-sm text-primary me-2' role='status'>
                            <span class='visually-hidden'>Loading...</span>
                        </div>
                        <span class='text-muted'>Loading geofence states...</span>
                    </div>
                </div>
            );
        }

        if (!geofenceData) {
            return null;
        }

        const { totalStates, totalContestants, byContestant = [], checkpointsByClass = {}, raceClasses = [] } = geofenceData;

        // Get checkpoints for selected race class
        const checkpoints = checkpointsByClass[selectedRaceClass] || [];

        // Filter contestants by selected race class
        const filteredContestants = byContestant.filter(c => c.raceClassId === selectedRaceClass);

        return (
            <div class={`mx-3 mt-2 mb-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-light'} border border-info`}>
                {/* Header */}
                <div
                    class='d-flex flex-wrap align-items-center justify-content-between p-3 cursor-pointer'
                    onClick={this.toggleCollapsed}
                    style='cursor: pointer;'
                >
                    <div class='d-flex align-items-center'>
                        <i class='fa-solid fa-satellite-dish text-info me-2' style='font-size: 1.2em;' />
                        <span class='fw-bold'>Geofence States</span>
                        <span class='badge bg-info ms-2'>Super Admin Debug</span>
                    </div>
                    <div class='d-flex align-items-center gap-2'>
                        <span class='badge bg-secondary'>
                            <i class='fa-solid fa-users me-1' />{totalContestants}
                        </span>
                        <span class='badge bg-secondary'>
                            <i class='fa-solid fa-map-marker-alt me-1' />{totalStates}
                        </span>
                        <button
                            type='button'
                            class='btn btn-sm btn-link p-0 ms-2'
                            onClick={(e) => { e.stopPropagation(); this.loadGeofenceStates(); }}
                            title='Refresh geofence states'
                        >
                            <i class='fa-solid fa-refresh' />
                        </button>
                        <i class={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'} ms-2`} />
                    </div>
                </div>

                {/* Collapsible content */}
                {!collapsed && (
                    <div class='px-3 pb-3'>
                        {/* Race class selector */}
                        {raceClasses.length > 1 && (
                            <div class='mb-2'>
                                <select
                                    class='form-select form-select-sm'
                                    value={selectedRaceClass}
                                    onChange={this.selectRaceClass}
                                    onClick={(e) => e.stopPropagation()}
                                    style='width: auto;'
                                >
                                    {raceClasses.map(rc => (
                                        <option key={rc.id} value={rc.id}>{rc.title}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Legend */}
                        <div class='mb-2 d-flex flex-wrap gap-2' style='font-size: 0.75em;'>
                            <span><span class='badge bg-success'><i class='fa-solid fa-check' /></span> Passed</span>
                            <span><span class='badge bg-info'><i class='fa-solid fa-location-dot' /></span> At CP</span>
                            <span><span class='badge bg-primary'><i class='fa-solid fa-satellite-dish' /></span> GPS (no result)</span>
                            <span><span class='badge bg-warning text-dark'><i class='fa-solid fa-unlock' /></span> Next CP</span>
                            <span><span class='badge bg-success'><i class='fa-solid fa-flag-checkered' /></span> Finished</span>
                        </div>

                        {filteredContestants.length === 0 ? (
                            <div class='text-muted'>
                                <i class='fa-solid fa-info-circle me-2' />
                                No geofence states found for this race class.
                            </div>
                        ) : (
                            <div class='table-responsive'>
                                <table class='table table-sm table-striped table-hover mb-0' style='font-size: 0.8em;'>
                                    <thead>
                                        <tr>
                                            <th style='width: 40px;'>Bib</th>
                                            <th>Name</th>
                                            {checkpoints.map((cp, idx) => (
                                                <th key={idx} class='text-center' style='width: 45px;' title={cp.name}>
                                                    {idx === 0 ? 'S' : (idx === checkpoints.length - 1 ? 'F' : idx)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredContestants.map((contestant, idx) => {
                                            const unlockedMd5 = this.getUnlockedCheckpoint(contestant, checkpoints);
                                            return (
                                                <tr key={idx}>
                                                    <td>
                                                        <span class='badge bg-dark'>{contestant.bib || '?'}</span>
                                                    </td>
                                                    <td class='text-truncate' style='max-width: 150px;'>
                                                        {contestant.name || 'Unknown'}
                                                    </td>
                                                    {checkpoints.map((cp, cpIdx) => {
                                                        const geoState = this.getStateForCheckpoint(contestant, cp.md5);
                                                        const actualResult = this.getActualResult(contestant, cp.md5);
                                                        const isUnlocked = cp.md5 === unlockedMd5;
                                                        const isFinish = cpIdx === checkpoints.length - 1;
                                                        const isStart = cpIdx === 0;
                                                        return (
                                                            <td key={cpIdx} class='text-center'>
                                                                {this.renderCellStatus(actualResult, geoState, isUnlocked, isFinish, isStart)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Raw JSON for Claude */}
                        <div class='mt-3 border-top pt-3'>
                            <div class='d-flex justify-content-between align-items-center mb-2'>
                                <h6 class='mb-0' style='cursor: pointer;' onClick={this.toggleRawJson}>
                                    <i class={`fa-solid ${this.state.showRawJson ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} />
                                    Raw JSON for Claude
                                </h6>
                                {this.state.showRawJson && (
                                    <button
                                        type='button'
                                        class={`btn btn-sm ${this.state.copied ? 'btn-success' : 'btn-outline-primary'}`}
                                        onClick={this.copyToClipboard}
                                    >
                                        <i class={`fa-solid ${this.state.copied ? 'fa-check' : 'fa-copy'} me-1`} />
                                        {this.state.copied ? 'Copied!' : 'Copy to clipboard'}
                                    </button>
                                )}
                            </div>
                            {this.state.showRawJson && (
                                <>
                                    <div class='text-body-secondary small mb-2'>
                                        Copy this and paste it into Claude to get help debugging:
                                    </div>
                                    <textarea
                                        class='form-control font-monospace'
                                        style='font-size: 0.7em; height: 400px; white-space: pre;'
                                        readOnly
                                        value={JSON.stringify({
                                            race: (() => {
                                                const { raceStore } = this.props.stores;
                                                const { race: r } = raceStore;
                                                return {
                                                    id: r.id,
                                                    title: r.title,
                                                    startDate: r.startDate,
                                                    endDate: r.endDate,
                                                    addAutoResultsHidden: r.addAutoResultsHidden,
                                                    showResults: r.showResults,
                                                    showTracking: r.showTracking,
                                                };
                                            })(),
                                            geofenceData,
                                        }, null, 2)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default GeofenceStateList;
